import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import shallow from 'zustand/shallow';

import useShareTradeModal from 'features/sharetrade/stores/useShareTradeModal';

import {
  trackDoneSelected,
  trackOrderViewed,
  trackShareSelected,
  trackTradeSharedViewed,
} from 'features/sharetrade/analytics/shareTradeShareTracker';
import tradingModalStore from 'global/TradingModal/store';
import tradingDataStore from 'lib/stores/tradingData';
import useShareTradeShare from 'stores/sharetrade/useShareTradeShare';
import useShareTradeTarget from 'stores/sharetrade/useShareTradeTarget';
import { getPreviewData } from 'features/sharetrade/utils/shareTradeOrder';
import { TargetShareItem } from 'typings/api/shareTradeShare';

import ModalShareSelect from '../ModalShareTrade';
import ModalShareConfirm from '../ModalShareConfirm';
import ModalShareSuccess from '../ModalSuccessInfo';

enum ShowModal {
  SELECT = 'SELECT',
  CONFIRM = 'CONFIRM',
  SUCCESS = 'SUCCESS',
}

const ModalShareTradeManager = () => {
  const router = useRouter();
  const [isAutoShareOn, setIsAutoShareOn] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<ShowModal>(null);

  const symbol = tradingModalStore((s) => s.symbol);
  const resetModal = tradingModalStore((s) => s.resetModal);

  const usersData = useShareTradeTarget((s) => s.data);

  const resetModalData = tradingDataStore((s) => s.resetModalData);
  const resetCompany = tradingDataStore((s) => s.resetCompany);

  const [isVisible, setIsVisible] = useShareTradeModal(
    (s) => [s.isVisible, s.setIsVisible],
    shallow,
  );

  const [selectedUsers, sharedUsers, setSelectedUsers, setSharedUsers] =
    useShareTradeModal(
      (s) => [
        s.selectedUsers,
        s.sharedUsers,
        s.setSelectedUsers,
        s.setSharedUsers,
      ],
      shallow,
    );

  const [
    transactionData,
    isShareLoading,
    loadShareTradeShare,
    setTransactionData,
  ] = useShareTradeShare(
    (s) => [
      s.transactionData,
      s.isLoading,
      s.loadShareTradeShare,
      s.setTransactionData,
    ],
    shallow,
  );

  const previewShareTrade = useMemo(
    () => getPreviewData(transactionData),
    [transactionData],
  );

  const userSelectedList = useMemo(() => {
    if (!usersData?.targets) return [];

    return usersData.targets
      .filter((user) => selectedUsers[user.id])
      .map((user) => ({
        ...user,
        ...selectedUsers[user.id],
      }));
  }, [usersData?.targets, selectedUsers]);

  const handleCloseModalShare = () => {
    setIsVisible(false);
    setShowModal(null);
    setSelectedUsers({});
  };

  const handleSubmitSelect = (isAddAutoShare: boolean) => {
    setIsAutoShareOn(isAddAutoShare);
    setShowModal(ShowModal.CONFIRM);
  };

  const handleCancelConfirm = () => {
    setShowModal(ShowModal.SELECT);
  };

  const handleShareConfirm = async () => {
    const postData = {
      ...transactionData,
      targets: Object.values(selectedUsers).map(
        ({ id, type, is_share_value }) =>
          ({ id, type, is_share_value } as TargetShareItem),
      ),
      save_as_autoshare: isAutoShareOn,
    };

    delete postData.total_investment;

    await loadShareTradeShare(postData);

    setSharedUsers({
      ...sharedUsers,
      ...selectedUsers,
    });
    setSelectedUsers({});
    setShowModal(ShowModal.SUCCESS);
    trackShareSelected(symbol, isAutoShareOn);
  };

  const handleSwitchChange = (checked: boolean, user: TargetShareItem) => {
    setSelectedUsers({
      ...selectedUsers,
      [user.id]: { ...user, is_share_value: checked },
    });
  };

  const handleShareSuccess = () => {
    handleCloseModalShare();
    trackDoneSelected(symbol);
  };

  const handleBackToChatList = () => {
    // Reset all modal states
    resetCompany();
    resetModal();
    resetModalData();

    // Reset modal share
    setTransactionData(undefined);
    handleCloseModalShare();
    setSelectedUsers({});
    setSharedUsers({});

    // Redirect to chat
    router.push('/chat');
  };

  useEffect(() => {
    if (isVisible) setShowModal(ShowModal.SELECT);
  }, [isVisible]);

  useEffect(() => {
    if (showModal === ShowModal.SELECT) trackOrderViewed();
    if (showModal === ShowModal.SUCCESS) trackTradeSharedViewed();
  }, [showModal]);

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      <ModalShareSelect
        visible={showModal === ShowModal.SELECT}
        modalVisible={isVisible}
        onCancel={handleCloseModalShare}
        onSubmit={handleSubmitSelect}
        zIndex={1003}
      />
      <ModalShareConfirm
        visible={showModal === ShowModal.CONFIRM}
        selectedUsers={userSelectedList}
        previewShareTrade={previewShareTrade}
        isLoading={isShareLoading}
        onCancel={handleCancelConfirm}
        onConfirm={handleShareConfirm}
        onSwitchChange={handleSwitchChange}
        zIndex={1004}
      />
      <ModalShareSuccess
        visible={showModal === ShowModal.SUCCESS}
        onCancel={handleShareSuccess}
        onSuccess={handleShareSuccess}
        onBack={handleBackToChatList}
      />
    </>
  );
};

export default ModalShareTradeManager;
